import { useVirtualizer } from '@tanstack/react-virtual';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  dateKeyFromIsoDate,
  isoDateFromDateKey,
  logEntryErrorCode,
  logEntryMetaExtras,
  logEntryReason,
  type AppLogEntry,
} from '@visualitics-toolkit/shared';

import { Badge, EmptyState, Input, Select } from '../components/atoms';
import {
  JsonExportButton,
  LoadingBlock,
  formatDateTime,
  formatDuration,
  useDebounced,
} from '../components/molecules';
import { useLogDays, useLogs } from '../lib/queries';

const LEVELS: { value: string; label: string }[] = [
  { value: '', label: 'Todos' },
  { value: 'error', label: 'Erro' },
  { value: 'warn', label: 'Aviso' },
  { value: 'info', label: 'Info' },
  { value: 'debug', label: 'Debug' },
  { value: 'verbose', label: 'Verbose' },
];

export function LogsPage(): ReactNode {
  const days = useLogDays();
  const [date, setDate] = useState<string>('');
  const [level, setLevel] = useState('');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search, 300);

  useEffect(() => {
    if (!date && days.data?.length) setDate(days.data[0].dateKey);
  }, [days.data, date]);

  const logs = useLogs({
    date: date || undefined,
    level: level || undefined,
    search: debouncedSearch || undefined,
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const entries = logs.data ?? [];
  const virtualizer = useVirtualizer({
    count: entries.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => 72,
    overscan: 12,
  });

  const knownDays = days.data ?? [];
  const hasFileForDate = !date || knownDays.some((day) => day.dateKey === date);
  const isoDate = date ? (isoDateFromDateKey(date) ?? '') : '';

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-content">Logs da aplicação</h1>
          {entries.length > 0 && (
            <p className="text-xs text-muted">
              {entries.length} {entries.length === 1 ? 'entrada' : 'entradas'}
              {logs.isFetching ? ' · atualizando…' : ''}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <label className="space-y-1 text-xs text-muted">
            <span className="block font-medium">Dia</span>
            <Select
              aria-label="Dia"
              value={knownDays.some((day) => day.dateKey === date) ? date : ''}
              onChange={(event) => setDate(event.target.value)}
            >
              {knownDays.length === 0 && <option value="">Nenhum arquivo ainda</option>}
              {knownDays.map((day) => (
                <option key={day.dateKey} value={day.dateKey}>
                  {day.dateKey}
                </option>
              ))}
            </Select>
            <Input
              type="date"
              aria-label="Escolher outro dia"
              value={isoDate}
              onChange={(event) => {
                const key = dateKeyFromIsoDate(event.target.value);
                if (key) setDate(key);
              }}
            />
          </label>
          <label className="space-y-1 text-xs text-muted">
            <span className="block font-medium">Nível</span>
            <Select aria-label="Nível" value={level} onChange={(event) => setLevel(event.target.value)}>
              {LEVELS.map((option) => (
                <option key={option.value || 'all'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </label>
          <label className="space-y-1 text-xs text-muted">
            <span className="block font-medium">Busca</span>
            <Input
              aria-label="Busca"
              placeholder="evento, código, motivo…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
          <JsonExportButton data={entries} filename={`logs-dia-${date || 'atual'}.json`} />
        </div>
      </header>

      {logs.isLoading && entries.length === 0 && <LoadingBlock />}
      {!logs.isLoading && !hasFileForDate && (
        <EmptyState title="Nenhum arquivo de log neste dia" description="Escolha outro dia ou aguarde novas entradas." />
      )}
      {!logs.isLoading && hasFileForDate && entries.length === 0 && (
        <EmptyState title="Nenhuma entrada" description="Sem registros para os filtros selecionados." />
      )}

      {entries.length > 0 && (
        <div ref={containerRef} className="max-h-[70vh] overflow-auto rounded-md border border-border">
          <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
            {virtualizer.getVirtualItems().map((virtualRow) => {
              const entry = entries[virtualRow.index];
              return (
                <div
                  key={virtualRow.key}
                  className="absolute left-0 top-0 w-full border-b border-border/60"
                  style={{ transform: `translateY(${virtualRow.start}px)` }}
                  ref={virtualizer.measureElement}
                  data-index={virtualRow.index}
                >
                  <LogRow entry={entry} />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function LogRow({ entry }: { entry: AppLogEntry }): ReactNode {
  const reason = logEntryReason(entry);
  const errorCode = logEntryErrorCode(entry);
  const extras = logEntryMetaExtras(entry);
  const tone = levelTone(entry.level);

  return (
    <article
      className={
        tone === 'danger'
          ? 'border-l-2 border-l-severity-irreversible bg-severity-irreversible/5 px-3 py-2'
          : tone === 'warning'
            ? 'border-l-2 border-l-severity-destructive bg-severity-destructive/5 px-3 py-2'
            : 'px-3 py-2'
      }
    >
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="font-mono text-muted" title={entry.timestamp}>
          {formatDateTime(entry.timestamp)}
        </span>
        <Badge tone={tone}>{entry.level}</Badge>
        <span className="font-medium text-content">{entry.message}</span>
        {entry.operationId && <span className="font-mono text-muted">{entry.operationId}</span>}
        {entry.phase && <Badge>{entry.phase}</Badge>}
        {entry.durationMs !== undefined && (
          <span className="font-mono text-muted">{formatDuration(entry.durationMs)}</span>
        )}
        {entry.runId && <span className="font-mono text-muted">run {entry.runId}</span>}
        {errorCode && <span className="font-mono text-severity-irreversible">{errorCode}</span>}
      </div>

      {reason && (
        <p className="mt-1 whitespace-pre-wrap break-words text-sm text-content">{reason}</p>
      )}

      {extras && <MetaExtras extras={extras} />}
    </article>
  );
}

function MetaExtras({ extras }: { extras: Record<string, unknown> }): ReactNode {
  const [open, setOpen] = useState(false);
  const keys = Object.keys(extras);
  const simple = keys.every((key) => isScalar(extras[key]));

  if (simple) {
    return (
      <p className="mt-1 flex flex-wrap gap-x-3 font-mono text-[11px] text-muted">
        {keys.map((key) => (
          <span key={key}>
            {key}: {String(extras[key])}
          </span>
        ))}
      </p>
    );
  }

  return (
    <div className="mt-1">
      <button
        type="button"
        className="text-[11px] text-accent underline"
        onClick={() => setOpen((current) => !current)}
      >
        {open ? 'Ocultar meta' : 'Ver meta'}
      </button>
      {open && (
        <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-words font-mono text-[11px] text-muted">
          {JSON.stringify(extras, null, 2)}
        </pre>
      )}
    </div>
  );
}

function isScalar(value: unknown): boolean {
  return value === null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
}

function levelTone(level: string): 'danger' | 'warning' | 'accent' | 'neutral' {
  if (level === 'error') return 'danger';
  if (level === 'warn') return 'warning';
  if (level === 'info') return 'accent';
  return 'neutral';
}
